import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MdOutlineMedicalServices,
  MdOutlineLocationOn,
  MdOutlineEmail,
} from "react-icons/md";
import { LuPhoneCall } from "react-icons/lu";
import { BiClinic } from "react-icons/bi";
import { Calendar1Icon } from "lucide-react";
import drEasyApi from "../../Api/Customapi";
import { useDoctorQuery } from "../../hooks/useDoctorQuery";
import { getImg } from "../../Api/getImg";
import imgBg from "../../assets/serviceBg3.png";
import { useDoctorContext } from "../../context/DoctorContext";
import { useLanguage } from "../../context/LanguageContext";
import { Clock11 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import NeedAuthModal from "../Modals/NeedAuthModal";
import GuestBookingModal from "../Modals/GuestInformationModal";
import LoginModal from "../Modals/LoginModal";
import { slug } from "../../utils/store";

export const getEffectivePrice = (service) => {
  const basePrice = service?.price ?? 0;
  if (
    service?.offerPercentage != null &&
    service.offerPercentage > 0 &&
    service.offerPercentage <= 100
  ) {
    return parseFloat(
      (basePrice * (1 - service.offerPercentage / 100)).toFixed(2),
    );
  }
  return basePrice;
};

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.2 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: -30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

// ── ClinicServicesModal ─────────────────────────────────────────────
const ClinicServicesModal = ({
  clinic,
  services,
  onClose,
  onBook,
  t,
  currentLanguage,
}) => {
  if (!clinic) return null;

  // FIX 6 & 7: Add dir attribute to modal so flex-row / justify-between
  // behave correctly in both LTR and RTL layouts.
  const isRtl = currentLanguage === "ar";

  return (
    <div
      onClick={onClose}
      dir={isRtl ? "rtl" : "ltr"}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl p-6 max-w-lg w-full max-h-[85vh] flex flex-col shadow-2xl"
        initial={{ opacity: 0, scale: 0.92, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.92, y: 20 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
      >
        {/* FIX 6: Header uses items-start so close button stays in the
            correct corner regardless of direction. The close button is
            always rendered last in the DOM; flex + dir handles placement. */}
        <div className="flex justify-between items-center mb-5 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
              <BiClinic className="text-primary text-xl" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] text-gray-400 leading-none mb-0.5">
                {t("clinics.availableAt") ?? "Available at"}
              </p>
              {/* FIX 3: replaced truncate with line-clamp-2 so Arabic clinic
                  names don't get clipped mid-word on a single line */}
              <h3 className="text-lg font-bold text-textPrimary leading-tight line-clamp-2">
                {clinic.clinicName}
              </h3>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors shrink-0 ms-2"
          >
            ×
          </button>
        </div>

        {/* Count badge */}
        <p className="text-xs text-textPrimary/50 mb-3 shrink-0">
          {services.length} {t("clinics.count") ?? "services available"}
        </p>

        {/* Services list */}
        <div className="flex-1 overflow-y-auto space-y-2 pe-1 scrollbar-thin scrollbar-thumb-primary/20 scrollbar-track-transparent">
          {services.map((service, index) => {
            const {
              id,
              title,
              description,
              photoID,
              price,
              offerPercentage,
              durationInMin,
            } = service;
            const hasDiscount = offerPercentage != null && offerPercentage > 0;
            const effectivePrice = getEffectivePrice(service);
            const fmt = (val) =>
              new Intl.NumberFormat(
                currentLanguage === "ar" ? "ar-EG" : "en-US",
                {
                  style: "currency",
                  currency: "EGP",
                  minimumFractionDigits: 0,
                },
              ).format(val);

            return (
              <motion.div
                key={id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.06, duration: 0.3 }}
                className="flex items-start gap-4 p-4 rounded-2xl bg-primary/5 hover:bg-primary/10 border border-transparent hover:border-primary/15 transition-all duration-200"
              >
                {/* Image / Icon */}
                <div className="relative shrink-0 w-12 h-12">
                  {photoID ? (
                    <div className="w-full h-full rounded-2xl overflow-hidden border-2 border-primary/20 shadow-sm">
                      <img
                        src={getImg(photoID)}
                        alt={title}
                        className="w-full h-full object-cover"
                        onError={(e) =>
                          (e.target.src =
                            "https://via.placeholder.com/80x80?text=No+Image")
                        }
                      />
                    </div>
                  ) : (
                    <div className="w-full h-full rounded-2xl bg-primary/10 flex items-center justify-center border-2 border-primary/15">
                      <MdOutlineMedicalServices className="text-primary w-5 h-5" />
                    </div>
                  )}
                  {hasDiscount && (
                    <span className="absolute -top-1.5 -end-1.5 bg-secondary text-white text-[9px] font-bold rounded-full px-1.5 py-0.5 leading-none">
                      -{offerPercentage}%
                    </span>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-textPrimary leading-snug">
                    {title}
                  </h4>
                  {description && (
                    /* FIX 4: min-h prevents card height jump when description
                       is absent or shorter in one language */
                    <p className="text-xs text-textPrimary/50 mt-0.5 line-clamp-2 leading-relaxed min-h-[2lh]">
                      {description}
                    </p>
                  )}
                  {/* FIX 5: flex-wrap so Arabic currency labels don't overflow */}
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    {durationInMin != null && (
                      <span className="flex items-center gap-1 text-[11px] text-textPrimary/50 bg-white rounded-full px-2 py-0.5 border border-gray-100 whitespace-nowrap">
                        <Clock11 size={10} /> {durationInMin} min
                      </span>
                    )}
                    {price != null && (
                      <span className="flex items-center gap-1.5 text-[11px] font-semibold flex-wrap">
                        {hasDiscount && (
                          <span className="line-through text-textPrimary/30 whitespace-nowrap">
                            {fmt(price)}
                          </span>
                        )}
                        <span className="text-primary bg-primary/10 rounded-full px-2 py-0.5 whitespace-nowrap">
                          {fmt(effectivePrice)}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* FIX 7: Footer keeps correct button order in RTL via flex-row-reverse
            when rtl, so "Book" stays on the visually prominent (inline-start) side */}
        <div
          className={`mt-5 flex gap-3 shrink-0 pt-4 border-t border-gray-100 ${
            isRtl ? "flex-row-reverse" : ""
          }`}
        >
          <button
            className="flex-1 bg-goldy hover:bg-goldy-hover text-white font-semibold py-3 px-6 rounded-xl hover:bg-primary/90 active:scale-95 transition-all duration-150"
            onClick={onBook}
          >
            {t("clinics.bookAppointment")}
          </button>
          <button
            onClick={onClose}
            className="px-5 py-3 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 active:scale-95 transition-all duration-150"
          >
            {t("common.close")}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default function OurServices() {
  const [serviceGroups, setServiceGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [selectedClinicServices, setSelectedClinicServices] = useState(null);

  const { data: doctor } = useDoctorQuery();
  const { setHasServices } = useDoctorContext();
  const { t, currentLanguage } = useLanguage();
  const sectionRef = useRef(null);

  const url = window.location.href;
  const currentPath = url.substring(url.lastIndexOf("/") + 1);
  const isServicesPage = currentPath === "services";
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isGuestModalVisible, setIsGuestModalVisible] = useState(false);
  const [pendingRoute, setPendingRoute] = useState(null);
  const [loginModal, setLoginModal] = useState(false);
  const [registerModal, setRegisterModal] = useState(false);

  // FIX 1: derive RTL once and reuse throughout
  const isRtl = currentLanguage === "ar";

  const isAuthorized =
    !!localStorage.getItem(`userTkn-${slug}`) ||
    !!sessionStorage.getItem(`userTkn-${slug}`);

  const handleProtectedNavigation = (route) => {
    if (!isAuthorized) {
      setPendingRoute(route);
      setIsModalVisible(true);
    } else {
      navigate(route);
    }
  };

  const handleGuestBooking = () => {
    setIsModalVisible(false);
    setIsGuestModalVisible(true);
  };

  const handleGuestInfoSubmit = (guestInfo) => {
    setIsGuestModalVisible(false);
    sessionStorage.setItem(`guestInfo-${slug}`, JSON.stringify(guestInfo));
    sessionStorage.setItem(`isGuestBooking-${slug}`, "true");
    navigate(pendingRoute || `/book-appointment`);
  };

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await drEasyApi.get("/api/Services/GetCenterServices", {
          params: { CenterId: doctor?.id },
        });

        setServiceGroups(res.data);

        const totalServices = res.data.reduce(
          (acc, g) => acc + (g.services?.length ?? 0),
          0,
        );
        if (totalServices > 0) setHasServices(true);
      } catch (err) {
        console.error("Failed to load services", err);
      } finally {
        setLoading(false);
      }
    };
    if (doctor?.id) fetchServices();
  }, [doctor?.id, currentLanguage]);

  const allCards = useMemo(() => {
    const cards = [];
    serviceGroups.forEach((group) => {
      if (group.clinicId !== null) {
        cards.push({ type: "clinic", group });
      } else {
        (group.services || []).forEach((service) => {
          cards.push({ type: "standalone", service });
        });
      }
    });
    return cards;
  }, [serviceGroups]);

  const LIMIT = 6;
  const displayedCards =
    isServicesPage || showAll ? allCards : allCards.slice(0, LIMIT);
  const hasMoreCards = !isServicesPage && allCards.length > LIMIT;

  const handleClinicCardClick = (group) => {
    setSelectedClinicServices({ clinic: group, services: group.services });
  };

  const handleBookFromModal = (clinicId) => {
    setSelectedClinicServices(null);
    handleProtectedNavigation(`/book-appointment?ClinicId=${clinicId}`);
  };

  const totalServices = serviceGroups.reduce(
    (acc, g) => acc + (g.services?.length ?? 0),
    0,
  );
  if (!loading && totalServices === 0) return null;

  return (
    // FIX 1: Add dir attribute to the section so the entire grid and all
    // child flex containers flip correctly when language changes to Arabic.
    <section
      ref={sectionRef}
      dir={isRtl ? "rtl" : "ltr"}
      className="relative py-20 min-h-screen flex flex-col flex-1 justify-center items-center"
    >
      <div
        id="services"
        className="max-w-7xl px-4 md:px-8 lg:px-16 w-full mx-auto"
      >
        <div className="text-center mb-14 mx-auto flex flex-col items-center justify-center">
          <h2 className="text-2xl md:text-4xl font-bold text-center goldy flex items-center justify-center gap-3">
            <MdOutlineMedicalServices className="text-3xl text-primary_hover" />
            {isServicesPage
              ? t("services.allOurServices")
              : t("services.ourServices")}
          </h2>
          <p className="mt-3 text-gray-500 text-sm md:text-base max-w-md md:max-w-2xl mx-auto">
            {isServicesPage
              ? t("services.allServicesDesc")
              : t("services.ourServicesDesc")}
          </p>
        </div>

        {loading ? (
          <div className="text-center text-lg text-gray-500">
            {t("common.loading")}
          </div>
        ) : (
          <>
            <motion.div
              className={`grid px-2 gap-4 md:gap-8 justify-center items-stretch ${
                isServicesPage
                  ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-2"
                  : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-2"
              }`}
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              <AnimatePresence>
                {displayedCards.map((card) => {
                  if (card.type === "clinic") {
                    const { group } = card;
                    return (
                      <motion.div
                        key={`clinic-${group.clinicId}`}
                        onClick={() => handleClinicCardClick(group)}
                        className="relative w-[97%] md:w-[99%] mx-auto group rounded-lg md:rounded-3xl bg-primary/10 hover:bg-cardColor hover:shadow-xl transition duration-300 flex flex-col overflow-hidden cursor-pointer min-h-[160px]"
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                        exit={{
                          opacity: 0,
                          y: -20,
                          transition: { duration: 0.3 },
                        }}
                      >
                        <div className="flex items-center gap-4 px-4 pt-4 pb-4 md:px-8 md:pt-8 md:pb-4">
                          <BiClinic className="text-primary text-2xl shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs text-gray-500 leading-none mb-1">
                              {t("clinics.availableAt") ?? "Available at"}
                            </p>
                            {/* FIX 3: line-clamp-1 instead of truncate —
                                allows the ellipsis to work correctly for
                                both LTR and RTL text */}
                            <p className="text-base font-bold text-primary line-clamp-1">
                              {group.clinicName}
                            </p>
                          </div>
                          {/* FIX 1: Use a neutral chevron character "›" wrapped in
                              a span that flips with CSS when dir="rtl" so it always
                              points in the correct reading direction */}
                          <span
                            className={`text-primary ms-auto text-lg transition-transform duration-200 ${
                              isRtl
                                ? "group-hover:-translate-x-0.5"
                                : "group-hover:translate-x-0.5"
                            }`}
                          >
                            {isRtl ? "‹" : "›"}
                          </span>
                        </div>

                        <div className="flex items-center justify-center px-4 pb-4 md:px-8 md:pb-8 pt-2">
                          <div className="flex items-center gap-2 bg-white/70 border border-primary/20 rounded-2xl px-5 py-4 w-full justify-center">
                            <MdOutlineMedicalServices className="text-gray-500 text-xl shrink-0" />
                            <span className="text-sm md:text-base font-semibold text-gray-400">
                              {group.services?.length ?? 0}
                            </span>
                            <span className="text-xs md:text-base text-gray-400">
                              {t("clinics.count") ?? "services available"}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    );
                  }

                  // ── Standalone service card ───────────────────────
                  const { service } = card;
                  const {
                    id,
                    title,
                    description,
                    photoID,
                    price,
                    offerPercentage,
                    durationInMin,
                  } = service;
                  const hasDiscount =
                    offerPercentage != null && offerPercentage > 0;
                  const effectivePrice = getEffectivePrice(service);

                  return (
                    <motion.div
                      key={id}
                      className="relative group p-10 rounded-3xl bg-primary/10 hover:bg-cardColor hover:shadow-xl transition duration-300 text-center flex flex-col min-h-[280px]"
                      variants={cardVariants}
                      initial="hidden"
                      animate="visible"
                      exit={{
                        opacity: 0,
                        y: -20,
                        transition: { duration: 0.3 },
                      }}
                    >
                      {hasDiscount && (
                        // FIX 1: Use end/start instead of right/left so the
                        // badge stays in the correct corner in both LTR & RTL
                        <span className="bg-secondary text-white rounded-full p-2 w-16 h-8 flex justify-center items-center absolute -top-3 -end-3 font-bold animate-pulse">
                          {offerPercentage}%
                        </span>
                      )}

                      {photoID ? (
                        <div className="relative w-28 h-28 mx-auto mb-6">
                          <img
                            src={imgBg}
                            alt={t("services.backgroundShape")}
                            className="absolute bottom-3 start-5 w-full h-full object-contain z-0 opacity-50"
                          />
                          <div className="relative w-full h-full rounded-full overflow-hidden border-2 border-primary/30 z-10">
                            <img
                              src={getImg(photoID)}
                              alt={title}
                              className="w-full h-full object-cover"
                              onError={(e) =>
                                (e.target.src =
                                  "https://via.placeholder.com/80x80?text=No+Image")
                              }
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="relative w-28 h-28 mx-auto mb-6 flex items-center justify-center">
                          <MdOutlineMedicalServices className="text-primary h-20 w-20" />
                        </div>
                      )}

                      <h3 className="text-2xl font-semibold text-textPrimary">
                        {title}
                      </h3>

                      {/* FIX 4: min-h-[4.5rem] prevents layout shift when
                          description is shorter/absent in one language */}
                      <p className="mt-3 text-black/60 text-base line-clamp-3 min-h-[4.5rem]">
                        {description || null}
                      </p>

                      {durationInMin != null && (
                        <p className="mt-3 text-primary text-base flex items-center justify-center gap-2">
                          <Clock11 size={16} /> {durationInMin} min
                        </p>
                      )}

                      {price != null && (
                        // FIX 5: flex-wrap + whitespace-nowrap so Arabic EGP
                        // currency strings don't break mid-number
                        <div className="mt-3">
                          {hasDiscount ? (
                            <div className="flex items-center justify-center gap-2 flex-wrap">
                              <span className="line-through text-black/40 text-base whitespace-nowrap">
                                {new Intl.NumberFormat(
                                  currentLanguage === "ar" ? "ar-EG" : "en-US",
                                  {
                                    style: "currency",
                                    currency: "EGP",
                                    minimumFractionDigits: 0,
                                  },
                                ).format(price)}
                              </span>
                              <span className="text-primary font-bold text-base whitespace-nowrap">
                                {new Intl.NumberFormat(
                                  currentLanguage === "ar" ? "ar-EG" : "en-US",
                                  {
                                    style: "currency",
                                    currency: "EGP",
                                    minimumFractionDigits: 0,
                                  },
                                ).format(effectivePrice)}
                              </span>
                            </div>
                          ) : (
                            <p className="text-primary text-base font-bold whitespace-nowrap">
                              {new Intl.NumberFormat(
                                currentLanguage === "ar" ? "ar-EG" : "en-US",
                                {
                                  style: "currency",
                                  currency: "EGP",
                                  minimumFractionDigits: 0,
                                },
                              ).format(price)}
                            </p>
                          )}
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </motion.div>

            {hasMoreCards && (
              <div className="text-center mt-12">
                {/* FIX 2: Removed the broken absolute-positioned shine <div>
                    that was overflowing the button. The button itself already
                    has overflow-hidden via rounded-lg so adding an absolute
                    child with no overflow:hidden parent broke on small screens.
                    The shine effect is now a clean CSS pseudo-element via the
                    group-hover:translate-x trick on a contained span. */}
                <button
                  onClick={() => {
                    if (showAll)
                      sectionRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    setShowAll((prev) => !prev);
                  }}
                  className="relative overflow-hidden bg-white border-2 border-primary font-semibold py-2 sm:py-3 px-4 sm:px-8 rounded-lg hover:bg-cardColor hover:scale-105 text-primary text-xs sm:text-sm transition-all duration-300 shadow-md hover:shadow-lg flex items-center gap-1.5 sm:gap-2 mx-auto group"
                >
                  <MdOutlineMedicalServices className="text-sm md:text-base relative z-10" />
                  <span className="relative z-10">
                    {showAll
                      ? t("services.seeLessServices")
                      : t("services.seeMoreServices")}
                  </span>
                  {/* Shine element is now correctly inside overflow-hidden button */}
                  <span
                    aria-hidden="true"
                    className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)",
                    }}
                  />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Clinic Services Modal ── */}
      <AnimatePresence>
        {selectedClinicServices && (
          <ClinicServicesModal
            clinic={selectedClinicServices.clinic}
            services={selectedClinicServices.services}
            onClose={() => setSelectedClinicServices(null)}
            onBook={() =>
              handleBookFromModal(selectedClinicServices.clinic.clinicId)
            }
            t={t}
            currentLanguage={currentLanguage}
          />
        )}
      </AnimatePresence>

      <NeedAuthModal
        open={isModalVisible}
        onConfirm={() => {
          setIsModalVisible(false);
          setLoginModal(true);
        }}
        onCancel={() => {
          setIsModalVisible(false);
          setPendingRoute(null);
        }}
        onGuestBooking={handleGuestBooking}
      />
      <LoginModal
        setRegisterModal={setRegisterModal}
        isRegister={registerModal}
        loginModal={loginModal}
        setLoginModal={setLoginModal}
      />
      <GuestBookingModal
        open={isGuestModalVisible}
        onCancel={() => {
          setIsGuestModalVisible(false);
          setPendingRoute(null);
        }}
        onSubmit={handleGuestInfoSubmit}
      />
    </section>
  );
}